import { useMutation } from '@tanstack/react-query';
import { handleErrorResponse } from '../../utils/handleError';
import { updateProfile } from '../../apis';
import { AxiosError } from 'axios';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { updateUserData } from '../../redux/slices/authSlice';
import queryClient from '../../lib/queryClient';

interface ProfileDetails
  extends Record<
    string,
    string | number | object | null | undefined | boolean
  > {
  patient_platform_first_login?: boolean;
}

const useUpdateProfile = () => {
  const { id: userId } =
    useAppSelector((state) => state.auth.currentUser) || {};
  const dispatch = useAppDispatch();
  const { mutate, error, isPending, status, isError, isSuccess } = useMutation({
    mutationKey: ['profile', userId],
    mutationFn: updateProfile,
    onSuccess: (data) => {
      const userData = data?.data || {};
      dispatch(
        updateUserData({
          patientPlatformFirstLogin: userData.patient_platform_first_login,
        })
      );

      queryClient.invalidateQueries({
        queryKey: ['profile', userId],
      });
    },
    onError: (error: AxiosError) => {
      handleErrorResponse(error, 'Something went wrong');
    },
  });

  const updateProfileDetails = (
    { patient_platform_first_login, ...rest }: ProfileDetails,
    setEditMode: () => void
  ) => {
    if (userId)
      mutate(
        {
          patient_platform_first_login: !!patient_platform_first_login,
          userId,
          ...rest,
        },
        {
          onSuccess: () => {
            if (setEditMode) setEditMode();
          },
        }
      );
  };

  return {
    updateProfileDetails,
    isError,
    isSuccess,
    error,
    isPending,
    status,
  };
};

export default useUpdateProfile;
